import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  ArrowRight,
  ArrowUp,
  BarChart2,
  LayoutGrid,
  Search,
  X,
} from "lucide-react";

import PortfolioGridCellCluster from "./PortfolioGridCellCluster";
import {
  AL_PORTFOLIO_ROWS,
  RL_LEVELS,
  clampLevel,
  formatLevel,
} from "../account-relationships/relationshipUtils";

const getGridRlColumn = account => {
  if (account.rlAggregate === null || account.rlAggregate === undefined)
    return "unknown";
  // Use floor when bucketing aggregate RL into integer columns so that
  // values like 2.9 remain in column 2 (RL2) instead of being rounded to 3.
  // Also clamp to the valid range 0..5 and handle non-finite values.
  const raw = Number(account.rlAggregate);
  if (!Number.isFinite(raw)) return "unknown";
  const column = Math.max(0, Math.min(5, Math.floor(raw)));
  return Number.isFinite(column) ? column : "unknown";
};

function getAccountAlPortfolioRow(account) {
  if (account.alLevel === null || account.alLevel === undefined)
    return "unknown";
  return clampLevel(account.alLevel, 0);
}

function portfolioAlRowBadgeClass(alRow) {
  if (alRow === "unknown") return "al-b al-unk";
  return `al-b al-${alRow}`;
}

function portfolioRlAxisBadgeClass(column) {
  if (column === "unknown") return "rl-b rl-unk";
  return `rl-b rl-${clampLevel(column)}`;
}

function normalizeSearchText(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function accountMatchesPortfolioSearch(account, query) {
  if (!query) return true;
  return normalizeSearchText(account?.name).includes(query);
}

const PG_Y_AXIS_LABEL = "Account Level (AL)";
const PG_X_AXIS_LABEL = "Relationship Level (RL)";

/**
 * AL×RL Zone Overlay — ported verbatim from my-accounts-portfolio-v4.html's
 * pgGetZone()/PG_ZONE_LABELS/PG_ZONE_DESCS/PG_ZONE_COLORS/PG_ZONE_ANCHORS
 * (mock lines 7254-7294). Only active when `enableZoneOverlay` is passed
 * (My Accounts Portfolio Goals tab) — the Relationships page's own
 * Portfolio Overview tab never sets data-zone, so it is unaffected.
 */
function pgGetZone(al, rl) {
  if (al === "unknown" || al == null || rl === "unknown" || rl == null)
    return null;
  const alNum = Number(al);
  const rlNum = Number(rl);
  const ab = alNum >= 4 ? "high" : alNum >= 2 ? "mid" : "low";
  const rb = rlNum >= 4 ? "strong" : rlNum >= 2 ? "moderate" : "weak";
  return (
    {
      "high-strong": "realized",
      "high-moderate": "spread-thin",
      "high-weak": "fragile-alignment",
      "mid-strong": "ready-advance",
      "mid-moderate": "balanced",
      "mid-weak": "anchor-missing",
      "low-strong": "warm-uncommitted",
      "low-moderate": "warm-thin",
      "low-weak": "no-foundation",
    }[`${ab}-${rb}`] || null
  );
}

const PG_ZONE_LABELS = {
  realized: "Realized ★",
  "spread-thin": "Aligned but Spread Thin",
  "fragile-alignment": "Fragile Alignment",
  "ready-advance": "Ready to Advance",
  balanced: "Balanced / On Track",
  "anchor-missing": "Alignment Without Anchor",
  "warm-uncommitted": "Warm but Uncommitted",
  "warm-thin": "Warm but Thin",
  "no-foundation": "No Foundation",
};

const PG_ZONE_DESCS = {
  realized:
    "Both AL and RL are at peak. Strategic alignment + strong exec relationships. Protect, compound, and expand into adjacent BUs.",
  "spread-thin":
    "High AL but moderate RL. Alignment likely rests on 1–2 strong pairs. Not stable at scale — broaden the relationship base.",
  "fragile-alignment":
    "High AL but thin RL. Most counterintuitive risk: one leadership change from collapse. Anchor alignment in personal relationships now.",
  "ready-advance":
    "Mid AL with strong RL. Relationship capital is in place to push alignment forward. Prime opportunity to advance AL.",
  balanced:
    "Both AL and RL developing in parallel. Expected operating state mid-journey. Normal track.",
  "anchor-missing":
    "Mid AL but weak RL. Alignment is developing but relationships cannot sustain it through disruption. A quieter risk.",
  "warm-uncommitted":
    "Low AL but strong RL. Most actionable mismatch — the hard work (building RL) is done. Activate relationships for alignment now.",
  "warm-thin":
    "Low AL, moderate RL. Rapport exists but alignment conversation has not happened. Opportunity being missed.",
  "no-foundation":
    "Both AL and RL at starting state. Expected for new accounts — a problem only if it persists beyond 2 weeks after assignment.",
};

const PG_ZONE_COLORS = {
  realized: "#16a34a",
  "spread-thin": "#d97706",
  "fragile-alignment": "#dc2626",
  "ready-advance": "#16a34a",
  balanced: "#2563eb",
  "anchor-missing": "#dc2626",
  "warm-uncommitted": "#ea5c0c",
  "warm-thin": "#d97706",
  "no-foundation": "#9ca3af",
};

/** Recommended direction copy (mock PG_ZONE_ACTIONS ~7864-7874). */
const PG_ZONE_ACTIONS = {
  realized:
    "Protect the realized state, compound current sponsors, and expand into adjacent BUs.",
  "spread-thin":
    "Broaden the relationship base so high alignment is not resting on one or two pairs.",
  "fragile-alignment":
    "Anchor alignment in personal relationships now, before a leadership change can collapse it.",
  "ready-advance":
    "Use existing relationship capital to push AL forward while the sponsors are already engaged.",
  balanced:
    "Keep AL and RL moving together; no structural correction is required.",
  "anchor-missing":
    "Build sustaining relationships so developing alignment can survive disruption.",
  "warm-uncommitted":
    "Activate existing relationships to convert warmth into an alignment commitment.",
  "warm-thin": "Start the alignment conversation while rapport still exists.",
  "no-foundation":
    "Establish a baseline AL assessment and mapping within two weeks of assignment.",
};

/** Suggested continuation (mock PG_ZONE_NEXT ~7876-7886). */
const PG_ZONE_NEXT = {
  realized: "Review expansion signals and successor coverage.",
  "spread-thin": "Open Relationship Priorities for unmapped or thin pairs.",
  "fragile-alignment": "Review priority relationships and continuity contacts.",
  "ready-advance": "Set or advance the AL goal while RL remains strong.",
  balanced:
    "Continue the current AL/RL cadence and watch for drift off the diagonal.",
  "anchor-missing": "Map and assess the missing executive relationships.",
  "warm-uncommitted": "Use the strongest pair to open the AL conversation.",
  "warm-thin":
    "Schedule the first alignment conversation with the existing contacts.",
  "no-foundation":
    "Start with AL assessment and contact mapping before setting goals.",
};

/** Anchor cells: topmost AL in band × leftmost RL in band (mock line 7294). */
const PG_ZONE_ANCHORS = new Set([
  "5-0",
  "5-2",
  "5-4",
  "3-0",
  "3-2",
  "3-4",
  "1-0",
  "1-2",
  "1-4",
]);

/** Row-major zone reference order for the "shown" legend grid (mock 7307-7311). */
const PG_ZONE_REF_ORDER = [
  ["fragile-alignment", "spread-thin", "realized"],
  ["anchor-missing", "balanced", "ready-advance"],
  ["no-foundation", "warm-thin", "warm-uncommitted"],
];

/**
 * @param {object} props
 * @param {any[]} props.accounts
 * @param {(account: any) => void} props.onOpenAccountPreview
 * @param {boolean} [props.showMyRelationships]
 * @param {string} [props.portfolioGridOwnerLabel]
 * @param {boolean} [props.hideViewToggle]
 * @param {boolean} [props.hideSearchControls] When true, parent renders search (e.g. My Accounts above Level Mix).
 * @param {string} [props.searchTerm] Controlled search value (use with onSearchTermChange).
 * @param {(value: string) => void} [props.onSearchTermChange]
 * @param {boolean} [props.showSponsorRoleLegend]
 * @param {boolean} [props.forceExecutiveRing]
 * @param {(alRow: string | number) => void} [props.onAlAxisClick]
 * @param {(rlColumn: string | number) => void} [props.onRlAxisClick]
 * @param {string | number | null} [props.activeAlAxis]
 * @param {string | number | null} [props.activeRlAxis]
 * @param {boolean} [props.enableZoneOverlay] When true (My Accounts Portfolio Goals tab
 *   only), renders the AL×RL strategic Zone Overlay toggle + cell shading
 *   from my-accounts-portfolio-v4.html. Off by default so the Relationships
 *   page's Portfolio Overview tab is unaffected.
 * @param {number} [props.bubbleSize] Single (non-clustered) grid bubble
 *   diameter in px. Default 52 (this shared component's original size,
 *   used by the Relationships page). My Accounts passes 42 to match
 *   my-accounts-portfolio-v4.html's `.acct-bubble` (mock line 1558).
 */
export function PortfolioOverviewTab({
  accounts,
  onOpenAccountPreview,
  showMyRelationships = false,
  portfolioGridOwnerLabel,
  /** When true (My Accounts Portfolio Map), hide Grid/EvR toggle and chrome not in the HTML prototype. */
  hideViewToggle = false,
  hideSearchControls = false,
  searchTerm: searchTermProp,
  onSearchTermChange,
  showSponsorRoleLegend = false,
  forceExecutiveRing = false,
  onAlAxisClick,
  onRlAxisClick,
  activeAlAxis = null,
  activeRlAxis = null,
  enableZoneOverlay = false,
  bubbleSize = 52,
}) {
  const [view, setView] = useState("portfolio");
  const [zoneOverlayOn, setZoneOverlayOn] = useState(false);
  const [zoneDetail, setZoneDetail] = useState(null);
  const [internalSearchTerm, setInternalSearchTerm] = useState("");
  const searchControlled = typeof onSearchTermChange === "function";
  const searchTerm = searchControlled
    ? (searchTermProp ?? "")
    : internalSearchTerm;
  const setSearchTerm = searchControlled
    ? onSearchTermChange
    : setInternalSearchTerm;
  const rlColumns = useMemo(() => ["unknown", ...RL_LEVELS], []);
  const searchQuery = normalizeSearchText(searchTerm);
  const activeView = hideViewToggle ? "portfolio" : view;

  useEffect(() => {
    if (!zoneOverlayOn) setZoneDetail(null);
  }, [zoneOverlayOn]);

  const showZoneDetail = useCallback((e, zone) => {
    if (!PG_ZONE_LABELS[zone]) return;
    const box = e.currentTarget.getBoundingClientRect();
    const detailW = Math.min(310, window.innerWidth - 24);
    const detailH = 220;
    let left = box.right + 10;
    if (left + detailW > window.innerWidth - 10) {
      left = box.left - detailW - 10;
    }
    left = Math.max(10, left);
    let top = box.top;
    if (top + detailH > window.innerHeight - 10) {
      top = window.innerHeight - detailH - 10;
    }
    setZoneDetail({ zone, top: Math.max(10, top), left });
  }, []);

  const hideZoneDetail = useCallback(() => setZoneDetail(null), []);

  const matchingIds = useMemo(() => {
    if (!searchQuery) return null;
    return new Set(
      accounts
        .filter(account => accountMatchesPortfolioSearch(account, searchQuery))
        .map(account => String(account.id))
    );
  }, [accounts, searchQuery]);

  const grid = useMemo(() => {
    const rows = {};
    AL_PORTFOLIO_ROWS.forEach(alKey => {
      rows[alKey] = {};
      rlColumns.forEach(col => {
        rows[alKey][col] = [];
      });
    });

    const seen = new Set();
    accounts.forEach(account => {
      const accountId = String(account?.id ?? "");
      if (!accountId || seen.has(accountId)) return;
      seen.add(accountId);

      const alKey = getAccountAlPortfolioRow(account);
      const rl = getGridRlColumn(account);
      if (!rows[alKey]) rows[alKey] = {};
      if (!rows[alKey][rl]) rows[alKey][rl] = [];
      rows[alKey][rl].push(account);
    });

    if (matchingIds) {
      Object.keys(rows).forEach(alKey => {
        Object.keys(rows[alKey]).forEach(col => {
          rows[alKey][col].sort((a, b) => {
            const aMatch = matchingIds.has(String(a.id));
            const bMatch = matchingIds.has(String(b.id));
            if (aMatch !== bMatch) return aMatch ? -1 : 1;
            return String(a.name || "").localeCompare(String(b.name || ""));
          });
        });
      });
    }

    return rows;
  }, [accounts, rlColumns, matchingIds]);

  const assessed = useMemo(() => {
    const seen = new Set();
    return accounts.filter(account => {
      const accountId = String(account?.id ?? "");
      if (!accountId || seen.has(accountId)) return false;
      seen.add(accountId);
      return account.rlAggregate !== null && account.rlAggregate !== undefined;
    }).length;
  }, [accounts]);

  const uniqueAccountCount = useMemo(() => {
    const seen = new Set();
    return accounts.filter(account => {
      const accountId = String(account?.id ?? "");
      if (!accountId || seen.has(accountId)) return false;
      seen.add(accountId);
      return true;
    }).length;
  }, [accounts]);

  const pending = Math.max(0, uniqueAccountCount - assessed);

  const portfolioSearchControls = (
    <>
      <div className="acct-picker-search pg-controls-search">
        <Search className="acct-picker-search-icon" aria-hidden />
        <input
          type="search"
          role="searchbox"
          className="acct-picker-search-input"
          placeholder="Search accounts..."
          value={searchTerm}
          onChange={event => setSearchTerm(event.target.value)}
          aria-label="Search accounts on portfolio grid"
        />
        {searchTerm ? (
          <button
            type="button"
            className="acct-picker-search-clear"
            onClick={() => setSearchTerm("")}
            aria-label="Clear search"
          >
            <X size={14} aria-hidden />
          </button>
        ) : null}
      </div>
      <div className="pg-info">
        {uniqueAccountCount} accounts · {assessed} RL assessed · {pending}{" "}
        pending RL assessment
      </div>
    </>
  );

  return (
    <div>
      {hideViewToggle ? (
        hideSearchControls ? null : (
          <div className="pg-controls pg-controls--my-accounts">
            {portfolioSearchControls}
          </div>
        )
      ) : (
        <div className="pg-controls">
          <div className="pg-view-toggle">
            <button
              type="button"
              className={`pg-view-toggle__btn${activeView === "portfolio" ? " active" : ""}`}
              onClick={() => setView("portfolio")}
            >
              <LayoutGrid aria-hidden />
              Portfolio Grid
            </button>
            <button
              type="button"
              className={`pg-view-toggle__btn${activeView === "reality" ? " active" : ""}`}
              onClick={() => {
                setSearchTerm("");
                setView("reality");
              }}
            >
              <BarChart2 aria-hidden />
              Expectation vs Reality
            </button>
          </div>
          <div className="pg-controls-end">
            {activeView === "portfolio" ? portfolioSearchControls : null}
          </div>
        </div>
      )}

      {activeView === "reality" ? (
        <div className="scatter-wrap">
          <div className="rl-empty-state" style={{ minHeight: 380 }}>
            <div className="rl-empty-state-card" style={{ maxWidth: 540 }}>
              <div style={{ marginBottom: 12 }}>
                <div
                  className="rl-empty-state-icon-wrap"
                  style={{ margin: "0 auto" }}
                >
                  <BarChart2 className="rl-empty-state-icon" />
                </div>
              </div>
              <div style={{ marginBottom: 14 }}>
                <span
                  className="coming-soon-pill"
                  style={{ marginLeft: 0, display: "inline-block" }}
                >
                  Coming Soon
                </span>
              </div>
              <h3
                className="rl-empty-state-title"
                style={{ fontSize: 17, marginBottom: 10 }}
              >
                Expectation vs Reality
              </h3>
              <p className="rl-empty-state-desc" style={{ marginBottom: 20 }}>
                This view will compare your team&apos;s human perception — AL
                &amp; RL scores captured through assessments — against
                AI-calculated relationship reality derived from email signals,
                calendar activity, and CRM interactions.
              </p>
              <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
                <div
                  style={{
                    flex: 1,
                    background: "var(--brand-faint)",
                    border: "1px solid var(--brand-tint)",
                    borderRadius: 8,
                    padding: "10px 14px",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "var(--brand)",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      marginBottom: 4,
                    }}
                  >
                    Expectation
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--gray-700)",
                      lineHeight: 1.4,
                    }}
                  >
                    Human input via assessments
                  </div>
                </div>
                <div
                  style={{
                    flex: 1,
                    background: "var(--gray-50)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    padding: "10px 14px",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: "var(--gray-500)",
                      textTransform: "uppercase",
                      letterSpacing: "0.07em",
                      marginBottom: 4,
                    }}
                  >
                    Reality
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--gray-700)",
                      lineHeight: 1.4,
                    }}
                  >
                    AI agents &amp; interaction signals
                  </div>
                </div>
              </div>
              <p className="rl-empty-state-desc">
                Our team is training intelligent agents to calculate, predict,
                and surface insights from real-world data — so you can see
                exactly where perception meets reality.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <>
          {!hideViewToggle && searchQuery && matchingIds?.size === 0 ? (
            <p
              style={{
                fontSize: 12,
                color: "var(--text-muted)",
                margin: "0 0 10px",
              }}
            >
              No accounts match &ldquo;{searchTerm.trim()}&rdquo;
            </p>
          ) : null}
          <div
            className={
              hideViewToggle ? "pg-wrap pg-wrap--level-mix-al" : "pg-wrap"
            }
          >
            <div
              style={{
                padding: "10px 14px 6px",
                borderBottom: "1px solid var(--border)",
                fontSize: 11,
                color: "var(--text-muted)",
                background: "var(--gray-50)",
              }}
            >
              <strong style={{ color: "var(--gray-700)" }}>
                {portfolioGridOwnerLabel
                  ? `${portfolioGridOwnerLabel} AL vs RL Portfolio Grid`
                  : "AL vs RL Portfolio Grid"}
              </strong>{" "}
              — Click any account bubble for a quick preview; then choose
              Account Relationships
              {showMyRelationships ? " or My Relationships" : ""}. Rows: AL ? or
              AL 0–5 · Columns: RL ? or RL 0–5.
            </div>
            <div style={{ display: "flex", alignItems: "stretch" }}>
              <div className="pg-al-axis-label">
                <div className="pg-al-axis-title">
                  <span>{PG_Y_AXIS_LABEL}</span>
                  <ArrowUp
                    className="pg-axis-title-arrow pg-axis-title-arrow--up"
                    strokeWidth={2.25}
                    aria-hidden
                  />
                </div>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  className={`pg-grid pg-grid-axis-bottom${enableZoneOverlay && zoneOverlayOn ? " pg-zone-active" : ""}`}
                >
                  {AL_PORTFOLIO_ROWS.map(alRow => {
                    const isActiveAl =
                      activeAlAxis != null &&
                      String(activeAlAxis) === String(alRow);
                    return (
                      <React.Fragment key={`row-${String(alRow)}`}>
                        <div
                          className={`pg-row-hdr${onAlAxisClick ? " cursor-pointer hover:bg-purple-50" : ""}`}
                          style={{
                            background: isActiveAl
                              ? "#ede9fe"
                              : "var(--gray-50)",
                          }}
                          role={onAlAxisClick ? "button" : undefined}
                          tabIndex={onAlAxisClick ? 0 : undefined}
                          aria-pressed={onAlAxisClick ? isActiveAl : undefined}
                          onClick={
                            onAlAxisClick
                              ? () => onAlAxisClick(alRow)
                              : undefined
                          }
                          onKeyDown={
                            onAlAxisClick
                              ? e => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    onAlAxisClick(alRow);
                                  }
                                }
                              : undefined
                          }
                        >
                          <span
                            className={portfolioAlRowBadgeClass(alRow)}
                            style={{ fontSize: 11 }}
                            title={
                              alRow === "unknown" ? "Unassessed" : undefined
                            }
                          >
                            {alRow === "unknown"
                              ? "AL ?"
                              : formatLevel("AL", alRow)}
                          </span>
                        </div>
                        {rlColumns.map(column => {
                          const isUnkCol = column === "unknown";
                          const isUnkRowAl = alRow === "unknown";
                          const cellAccounts = grid[alRow]?.[column] || [];
                          const isClustered = cellAccounts.length >= 3;
                          let cellClass = "pg-cell";
                          if (isClustered) cellClass += " pg-cell--clustered";
                          if (isUnkCol && isUnkRowAl) cellClass += " unk-both";
                          else if (isUnkCol) cellClass += " unk-col";
                          else if (isUnkRowAl) cellClass += " unk-row";

                          // Zone Overlay attrs (mock lines 7399-7413) — only
                          // computed when enabled, so unrelated consumers pay
                          // no cost and set no data-zone attributes at all.
                          const zone = enableZoneOverlay
                            ? pgGetZone(alRow, column)
                            : null;
                          const isAlTop =
                            enableZoneOverlay &&
                            alRow !== "unknown" &&
                            [5, 3, 1].includes(Number(alRow));
                          const isRlLeft =
                            enableZoneOverlay &&
                            column !== "unknown" &&
                            [0, 2, 4].includes(Number(column));
                          const isAnchor =
                            enableZoneOverlay &&
                            alRow !== "unknown" &&
                            column !== "unknown" &&
                            PG_ZONE_ANCHORS.has(`${alRow}-${column}`);
                          const zoneAttrs = {};
                          if (zone) zoneAttrs["data-zone"] = zone;
                          if (isAlTop && zone) zoneAttrs["data-al-top"] = "";
                          if (isRlLeft && zone) zoneAttrs["data-rl-left"] = "";

                          return (
                            <div
                              key={`${String(alRow)}-${column}`}
                              className={cellClass}
                              {...zoneAttrs}
                            >
                              {/* Zone label is a direct child of .pg-cell so its
                                  containing block has real width. Inside
                                  .pg-cell-wrap (shrink-to-content), empty
                                  anchor cells collapsed to 0×0 and
                                  max-width:calc(100%-6px) hid the text. */}
                              {isAnchor && zone ? (
                                <div
                                  className={`pg-zone-lbl zone-${zone}`}
                                  onMouseEnter={e => showZoneDetail(e, zone)}
                                  onMouseLeave={hideZoneDetail}
                                >
                                  {PG_ZONE_LABELS[zone]}
                                </div>
                              ) : null}
                              <div
                                className={`pg-cell-wrap${isClustered ? " pg-cell-wrap--clustered" : ""}`}
                              >
                                <PortfolioGridCellCluster
                                  accounts={cellAccounts}
                                  alRow={alRow}
                                  rlColumn={column}
                                  onOpenAccount={onOpenAccountPreview}
                                  searchActive={Boolean(searchQuery)}
                                  matchingIds={matchingIds}
                                  searchQuery={searchTerm}
                                  forceExecutiveRing={forceExecutiveRing}
                                  bubbleSize={bubbleSize}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}

                  <div
                    className="pg-axis-corner pg-corner-rl-axis"
                    aria-hidden
                  />
                  {rlColumns.map(column => {
                    const isActiveRl =
                      activeRlAxis != null &&
                      String(activeRlAxis) === String(column);
                    return (
                      <div
                        key={`rl-axis-${column}`}
                        className={`pg-col-hdr pg-rl-axis-cell${onRlAxisClick ? " cursor-pointer hover:bg-purple-50" : ""}`}
                        style={{
                          background: isActiveRl ? "#ede9fe" : "var(--gray-50)",
                        }}
                        role={onRlAxisClick ? "button" : undefined}
                        tabIndex={onRlAxisClick ? 0 : undefined}
                        aria-pressed={onRlAxisClick ? isActiveRl : undefined}
                        onClick={
                          onRlAxisClick
                            ? () => onRlAxisClick(column)
                            : undefined
                        }
                        onKeyDown={
                          onRlAxisClick
                            ? e => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  onRlAxisClick(column);
                                }
                              }
                            : undefined
                        }
                      >
                        <span
                          className={portfolioRlAxisBadgeClass(column)}
                          title={
                            column === "unknown" ? "Unassessed" : undefined
                          }
                        >
                          {column === "unknown"
                            ? "RL ?"
                            : formatLevel("RL", column)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="pg-axis-x-title-bar">
              <span>{PG_X_AXIS_LABEL}</span>
              <ArrowRight
                className="pg-axis-title-arrow"
                strokeWidth={2.25}
                aria-hidden
              />
            </div>
            <div className="pg-legend">
              {[5, 4, 3, 2, 1, 0].map(al => (
                <div key={al} className="pg-legend-item">
                  <div
                    className="lg-dot"
                    style={
                      hideViewToggle
                        ? {
                            background: "#fff",
                            borderColor: `var(--al-${al}-border)`,
                          }
                        : {
                            background: `var(--rl-${al})`,
                            borderColor: `var(--rl-${al}-border)`,
                          }
                    }
                  />
                  AL {al}
                </div>
              ))}
              <div className="pg-legend-item" title="Unassessed">
                <div
                  className="lg-dot"
                  style={
                    hideViewToggle
                      ? {
                          background: "#fff",
                          borderColor: "var(--al-unk-border)",
                        }
                      : {
                          background: "var(--gray-100)",
                          borderColor: "var(--gray-300)",
                        }
                  }
                />
                AL ?
              </div>
              <div
                className="pg-legend-meta"
                style={{
                  marginLeft: "auto",
                  display: "inline-flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 6,
                  fontSize: hideViewToggle ? 10.5 : 11,
                  color: "var(--text-muted)",
                }}
              >
                <span>
                  Bubble color = AL · Position = RL floor · ▶ = progress within
                  band
                </span>
                {forceExecutiveRing ? (
                  <>
                    <span>·</span>
                    <span className="pg-legend-item">
                      <span
                        className="pg-legend-stroke pg-legend-stroke--none"
                        aria-hidden
                      />
                      Executive (dashed)
                    </span>
                  </>
                ) : showSponsorRoleLegend ? (
                  <>
                    <span>· Ring thickness =</span>
                    <span className="pg-legend-item">
                      <span
                        className="pg-legend-stroke pg-legend-stroke--thick"
                        aria-hidden
                      />
                      Primary
                    </span>
                    <span>/</span>
                    <span className="pg-legend-item">
                      <span
                        className="pg-legend-stroke pg-legend-stroke--thin"
                        aria-hidden
                      />
                      Secondary
                    </span>
                    <span>/</span>
                    <span className="pg-legend-item">
                      <span
                        className="pg-legend-stroke pg-legend-stroke--none"
                        aria-hidden
                      />
                      Executive (dashed)
                    </span>
                  </>
                ) : null}
                {enableZoneOverlay ? (
                  <>
                    <span>·</span>
                    <button
                      type="button"
                      id="pg-zone-toggle-btn"
                      className={`pg-zone-toggle-btn${zoneOverlayOn ? " active" : ""}`}
                      onClick={() => setZoneOverlayOn(v => !v)}
                      title="Show AL×RL strategic zone overlay on the grid"
                    >
                      Zone Overlay
                    </button>
                  </>
                ) : null}
              </div>
            </div>
            {enableZoneOverlay && zoneOverlayOn ? (
              <div
                className="pg-zone-ref shown"
                role="region"
                aria-label="AL×RL Zone Reference"
              >
                {PG_ZONE_REF_ORDER.flat().map(zoneKey => {
                  const color = PG_ZONE_COLORS[zoneKey];
                  return (
                    <div
                      key={zoneKey}
                      className="pg-zone-card"
                      data-zone-card={zoneKey}
                      tabIndex={0}
                      aria-describedby="pg-zone-detail"
                      style={{
                        background: `${color}10`,
                        borderLeftColor: color,
                      }}
                      onMouseEnter={e => showZoneDetail(e, zoneKey)}
                      onMouseLeave={hideZoneDetail}
                      onFocus={e => showZoneDetail(e, zoneKey)}
                      onBlur={hideZoneDetail}
                    >
                      <strong style={{ color }}>
                        {PG_ZONE_LABELS[zoneKey]}
                      </strong>
                      <span>{PG_ZONE_DESCS[zoneKey]}</span>
                      <div className="pg-zone-action-hint">
                        ⓘ Recommended direction
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>
          {zoneDetail ? (
            <div
              id="pg-zone-detail"
              role="tooltip"
              className="pm-guide-detail visible"
              style={{ top: zoneDetail.top, left: zoneDetail.left }}
            >
              <div className="pm-guide-detail-title">
                {PG_ZONE_LABELS[zoneDetail.zone]}
              </div>
              <div className="pm-guide-detail-copy">
                {PG_ZONE_DESCS[zoneDetail.zone]}
              </div>
              <div className="pm-guide-detail-action">
                <strong>Recommended direction</strong>
                {PG_ZONE_ACTIONS[zoneDetail.zone]}
              </div>
              {PG_ZONE_NEXT[zoneDetail.zone] ? (
                <div className="pm-guide-detail-next">
                  <strong>Suggested continuation:</strong>{" "}
                  {PG_ZONE_NEXT[zoneDetail.zone]}
                </div>
              ) : null}
            </div>
          ) : null}
          <div
            className="mt-3 text-sm"
            style={{ padding: "10px 0", color: "var(--text-muted)" }}
          >
            <strong style={{ color: "var(--gray-700)" }}>Tip: </strong>
            Click a bubble for preview. Cells with 3+ accounts show a stacked
            cluster (+N when more than 3) — click to pick from the list. RL ?
            means aggregate not assessed yet.
          </div>
        </>
      )}
    </div>
  );
}

export default PortfolioOverviewTab;
